import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ReactDOM from 'react-dom';
import {
  Form,
  Input,
  Button,
  Col,
  Row,
  Modal,
  message,
  Select,
  Empty,
  Checkbox,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  TagOutlined,
  DownOutlined,
  CloseOutlined,
  SearchOutlined,
  ShopOutlined,
  LoginOutlined,
  MedicineBoxOutlined,
  CoffeeOutlined,
  AppstoreOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';
import moment from 'moment';
import MapDrawing from '../../../shared/maps/MapDrawing';
import CreateAudienceForm from '../../OrgConfig/components/CreateAudienceForm';
import PaginationTable from '../../../shared/PaginationTable';
import { MapConfig } from '../../../shared/maps/configs/mapConfigs';
import { countryMapping, stateMapping } from '../../../../core/utils/constants/constants';

// ---------------------------------------------------------------------------
// CLASSIFICATION TAG CONFIG
// ---------------------------------------------------------------------------

const classificationTagConfig = {
  COMPLIANCE_MEDICAL: { color: 'green', label: 'Medical' },
  COMPLIANCE_RECREATIONAL: { color: 'blue', label: 'Recreational' },
  COMPLIANCE_BOTH: { color: 'purple', label: 'Both' },
};

// ---------------------------------------------------------------------------
// TABLE COLUMNS — Audiences
// ---------------------------------------------------------------------------

const AUDIENCE_COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (name, record) => {
      const count = record.geoTargets
        ? record.geoTargets.filter((t) => t.location).length
        : 0;
      return (
        <div className="rs-loc-row__name">
          <div className="rs-loc-row__name-text">{name}</div>
          <div className="rs-loc-row__name-sub">
            <span className="rs-audience-loc-badge">
              {count} location{count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    title: 'Date Created',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 160,
    render: (text) => (
      <span className="rs-loc-row__date">{moment(text).format('MM/DD/YYYY - hh:mma')}</span>
    ),
    sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
  },
  {
    title: 'Date Updated',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 160,
    render: (text) => (
      <span className="rs-loc-row__date">{moment(text).format('MM/DD/YYYY - hh:mma')}</span>
    ),
    sorter: (a, b) => moment(a.updatedAt).unix() - moment(b.updatedAt).unix(),
  },
];

// Expandable row for audience/classification tables
const ExpandedLocations = ({ record }) => {
  const locations = record.geoTargets
    ? record.geoTargets.filter((t) => t.location)
    : [];

  if (!locations.length) {
    return <div className="rs-expand-empty">No locations</div>;
  }

  return (
    <div className="rs-expand">
      {locations.map((target) => (
        <div key={target.id} className="rs-expand__item">
          <EnvironmentOutlined className="rs-expand__icon" />
          <span className="rs-expand__name">{target.name}</span>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// TABLE COLUMNS — Classification Groups
// ---------------------------------------------------------------------------

const CLASSIFICATION_COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (name, record) => {
      const count = record.geoTargets
        ? record.geoTargets.filter((t) => t.location).length
        : 0;
      return (
        <div className="rs-loc-row__name">
          <div className="rs-loc-row__name-text">{name}</div>
          <div className="rs-loc-row__name-sub">
            <span className="rs-audience-loc-badge">
              {count} location{count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    title: 'Classification',
    dataIndex: 'type',
    key: 'type',
    width: 150,
    render: (type) => {
      const config = classificationTagConfig[type] || { color: 'default', label: type };
      const color = type === 'COMPLIANCE_MEDICAL' ? '#16a34a'
        : type === 'COMPLIANCE_RECREATIONAL' ? '#2563eb'
        : type === 'COMPLIANCE_BOTH' ? '#7c3aed' : '#666';
      return (
        <span
          className="rs-loc-row__classification-badge"
          style={{
            background: `${color}14`,
            color,
            border: `1px solid ${color}33`,
          }}
        >
          {config.label}
        </span>
      );
    },
  },
  {
    title: 'Date Created',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 160,
    render: (text) => (
      <span className="rs-loc-row__date">{moment(text).format('MM/DD/YYYY - hh:mma')}</span>
    ),
    sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
  },
  {
    title: 'Date Updated',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 160,
    render: (text) => (
      <span className="rs-loc-row__date">{moment(text).format('MM/DD/YYYY - hh:mma')}</span>
    ),
    sorter: (a, b) => moment(a.updatedAt).unix() - moment(b.updatedAt).unix(),
  },
];

// ---------------------------------------------------------------------------
// TAB CONFIGURATION
// ---------------------------------------------------------------------------

const TAB_CONFIG = [
  { key: 'location', label: 'Addresses', icon: EnvironmentOutlined },
  { key: 'audience', label: 'Audiences', icon: TeamOutlined },
  { key: 'classification', label: 'Classification Groups', icon: TagOutlined },
];

// ---------------------------------------------------------------------------
// FORM PAGES (unchanged from LocationTab)
// ---------------------------------------------------------------------------

const FirstFormPage = ({ form, onChange, isSelfService }) => {
  return (
    <>
      <Form.Item
        label="Location Name"
        name="name"
        extra="Required name given to this location"
        rules={[{ required: true, message: 'Please input your Business name!' }]}
      >
        <Input
          placeholder="Enter retail store name"
          name="name"
          onChange={({ target }) => onChange([target.name], target.value)}
        />
      </Form.Item>
      <Form.Item
        label="Location Description"
        name="description"
        extra="Description for this location"
      >
        <Input
          placeholder="Optional description"
          name="description"
          onChange={({ target }) => onChange([target.name], target.value)}
        />
      </Form.Item>
      <Row gutter={[12, 0]}>
        <Col xs={24} xl={12}>
          <Form.Item
            label="Classification"
            name="designation"
            extra="Required classification for this location"
            rules={[{ required: true, message: 'Please select a classification!' }]}
          >
            <Select
              placeholder="Select Classification"
              onChange={(value) => onChange('designation', value)}
            >
              <Select.Option value="MEDICAL">Medical</Select.Option>
              <Select.Option value="RECREATIONAL">Recreational</Select.Option>
              <Select.Option value="BOTH">Both (Medical and Recreational)</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} xl={12}>
          <Form.Item
            label="State License ID"
            name="licenseIdentifier"
            extra="Optional state license identifier"
          >
            <Input
              placeholder="State License ID"
              name="licenseIdentifier"
              onChange={({ target }) => onChange([target.name], target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col xs={24} xl={24}>
          <Form.Item
            label="Location Key"
            name="locationKey"
            extra="Shopping cart URL string that identifies a retail location"
            rules={[{ required: !isSelfService, message: 'Please input the Location Key!' }]}
          >
            <Input
              placeholder="Location Key"
              name="locationKey"
              onChange={({ target }) => onChange([target.name], target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        label="Street Address"
        name="formattedAddress"
        extra="Search and then select the address of this location"
        rules={[{ required: true, message: 'Address is required!' }]}
      >
        <input
          type="input"
          placeholder="Address"
          name="formattedAddress"
          id="search-input-tl"
          className="ant-input"
          onFocus={(e) => e.target.setAttribute('autocomplete', 'no')}
        />
      </Form.Item>
      <Row gutter={[12, 0]}>
        <Col xs={24} xl={9}>
          <Form.Item label="City" name="city">
            <Input disabled />
          </Form.Item>
        </Col>
        <Col xs={24} xl={9}>
          <Form.Item label="Zip" name="zip">
            <Input disabled />
          </Form.Item>
        </Col>
        <Col xs={24} xl={6}>
          <Form.Item label="State" name="state">
            <Input disabled />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

const SecondFormPage = ({
  form,
  onChange,
  selectedGeoTargets,
  initialLocation,
  shouldClearShapes,
  toggleShouldClearShapes,
  changeLocationMap,
  selectedAddress,
  initialPosition,
  setHasOverlay,
  hasOverlay,
}) => {
  return (
    <>
      <Col xs={24} lg={24}>
        <MapDrawing
          inputPlaceholder={'Search Retail Location...'}
          zoom={17}
          shapes={selectedGeoTargets ? selectedGeoTargets : []}
          overlayChanged={(val) => {
            onChange('inProgressOverlay', val);
            setHasOverlay(val && val.length > 0);
          }}
          initialLocation={initialPosition ? initialPosition : initialLocation}
          shouldClearShapes={shouldClearShapes}
          clearedCallback={toggleShouldClearShapes}
          toolOptions={{
            fillColor: '#BCDCF9',
            fillOpacity: 0.5,
            strokeWeight: 3,
            strokeColor: '#57ACF9',
            clickable: true,
            editable: true,
            zIndex: 1,
          }}
          styles={{
            map: {
              height: '500px',
              width: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
            },
            mapContainer: {
              height: '510px',
              width: '100%',
              position: 'relative',
            },
          }}
          changeLocation={(place) => changeLocationMap(place)}
          drawing={true}
          isSearchBarVisible={false}
          mapType={'satellite'}
        />
      </Col>
    </>
  );
};

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

const RetailStoresLocationTab = ({
  locationList,
  parentState,
  changeLocationMap,
  onChange,
  createLocation,
  deleteLocation,
  resetParentState,
  updateOrgLocation,
  cartProvider,
  onChangeStateValue,
  selectedLocations,
  onSubmitAudience,
  attributionAudienceName,
  shouldClearShapes,
  toggleShouldClearShapes,
  selectedOrgId,
  attributionAudiences,
  deleteAudience,
  currentuser,
  google,
  isSelfService,
  complianceAudiences,
}) => {
  const history = useHistory();

  // ── Tab state ────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('location');
  const [toolbarContainer, setToolbarContainer] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navRef = useRef(null);
  const dropdownRef = useRef(null);
  const useDropdown = useMediaQuery({ query: '(max-width: 1337px)' });
  const isTabletOrBelow = useMediaQuery({ query: '(max-width: 992px)' }); // $breakpoint-lg
  const [visibleCards, setVisibleCards] = useState(20);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const cardSentinelRef = useRef(null);

  // ── Form & modal state (same as LocationTab) ────────────────────────
  const [currentStep, setCurrentStep] = useState(1);
  const [formShow, setFormShow] = useState(false);
  const [form] = Form.useForm();
  const [isAudienceModalVisible, setIsAudienceModalVisible] = useState(false);
  const [audienceType, setAudienceType] = useState('');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [initialPosition, setInitialPosition] = useState(null);
  const [hasOverlay, setHasOverlay] = useState(false);
  const [classificationTypeValue, setClassificationTypeValue] = useState('');
  const [classificationFilter, setClassificationFilter] = useState(null);
  const [isDeleteBulk, setIsDeleteBulk] = useState(false);
  const [addressSearchText, setAddressSearchText] = useState('');
  const [addressPage, setAddressPage] = useState(1);

  // ── Audiences tab state ─────────────────────────────────────────────
  const [audienceSelectedRows, setAudienceSelectedRows] = useState([]);
  const [audienceSearchText, setAudienceSearchText] = useState('');
  const [audienceDeletedIds, setAudienceDeletedIds] = useState([]);
  const [isAudienceDeleteOpen, setIsAudienceDeleteOpen] = useState(false);
  const [audiencePage, setAudiencePage] = useState(1);
  const [visibleAudienceCards, setVisibleAudienceCards] = useState(20);
  const [expandedAudienceCardId, setExpandedAudienceCardId] = useState(null);
  const audienceSentinelRef = useRef(null);

  // ── Classification groups tab state ─────────────────────────────────
  const [classSelectedRows, setClassSelectedRows] = useState([]);
  const [classSearchText, setClassSearchText] = useState('');
  const [classDeletedIds, setClassDeletedIds] = useState([]);
  const [isClassDeleteOpen, setIsClassDeleteOpen] = useState(false);
  const [classPage, setClassPage] = useState(1);
  const [visibleClassCards, setVisibleClassCards] = useState(20);
  const [expandedClassCardId, setExpandedClassCardId] = useState(null);
  const classSentinelRef = useRef(null);

  let locationIds = selectedLocations.map((loc) => loc.id);
  const hasSelection = selectedLocations && selectedLocations.length > 0;
  const hasAudienceSelection = audienceSelectedRows.length > 0;
  const hasClassSelection = classSelectedRows.length > 0;

  const handleClearSelection = () => {
    onChangeStateValue('selectedLocations', []);
  };

  // ── Audiences data extraction ───────────────────────────────────────
  const audienceEdges =
    attributionAudiences &&
    attributionAudiences.audiencesConnection &&
    attributionAudiences.audiencesConnection.edges &&
    attributionAudiences.audiencesConnection.edges.filter((val) => val.node.orgs.length === 1);
  const audienceRows = (audienceEdges || []).map((val) => val.node);
  const filteredAudienceRows = audienceRows
    .filter((a) => !audienceDeletedIds.includes(a.id))
    .filter(
      (a) =>
        a.orgs.length &&
        a.orgs[0].id === currentuser.role.org.id &&
        a.name.toLowerCase().includes(audienceSearchText.toLowerCase())
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const onDeleteAudiences = async () => {
    const ids = audienceSelectedRows.map((r) => r.id);
    if (!ids.length) return;
    const result = await deleteAudience(ids);
    if (result.successCount > 0) {
      setAudienceDeletedIds((prev) => [...prev, ...ids]);
      setAudienceSelectedRows([]);
    }
    setIsAudienceDeleteOpen(false);
  };

  // ── Classification groups data extraction ───────────────────────────
  const classEdges =
    complianceAudiences &&
    complianceAudiences.audiencesConnection &&
    complianceAudiences.audiencesConnection.edges &&
    complianceAudiences.audiencesConnection.edges.filter((val) => val.node.orgs.length === 1);
  const classRows = (classEdges || []).map((val) => val.node);
  const filteredClassRows = classRows
    .filter((a) => !classDeletedIds.includes(a.id))
    .filter(
      (a) =>
        a.orgs.length &&
        a.orgs[0].id === currentuser.role.org.id &&
        a.name.toLowerCase().includes(classSearchText.toLowerCase())
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const onDeleteClassifications = async () => {
    const ids = classSelectedRows.map((r) => r.id);
    if (!ids.length) return;
    const result = await deleteAudience(ids, { label: 'classification group' });
    if (result.successCount > 0) {
      setClassDeletedIds((prev) => [...prev, ...ids]);
      setClassSelectedRows([]);
    }
    setIsClassDeleteOpen(false);
  };

  const classificationFilterLabels = {
    MEDICAL: 'Medical',
    RECREATIONAL: 'Recreational',
    BOTH: 'Both',
  };

  const classificationToAudienceType = {
    MEDICAL: 'COMPLIANCE_MEDICAL',
    RECREATIONAL: 'COMPLIANCE_RECREATIONAL',
    BOTH: 'COMPLIANCE_BOTH',
  };

  // ── Filtered addresses for mobile card view ─────────────────────────
  const filteredAddresses = React.useMemo(() => {
    if (!locationList) return [];
    return locationList
      .filter(
        (loc) => loc.audienceType === 'ATTRIBUTION' && loc.org && loc.org.id === selectedOrgId
      )
      .filter((loc) => {
        if (!classificationFilter) return true;
        const designations =
          loc.licenses && loc.licenses[0] ? loc.licenses[0].designations || [] : [];
        const hasMedical = designations.includes('MEDICAL');
        const hasRecreational = designations.includes('RECREATIONAL');
        switch (classificationFilter) {
          case 'MEDICAL': return hasMedical;
          case 'RECREATIONAL': return hasRecreational;
          case 'BOTH': return hasMedical && hasRecreational;
          default: return true;
        }
      })
      .filter((loc) => {
        if (!addressSearchText) return true;
        const term = addressSearchText.toLowerCase();
        return (
          (loc.name || '').toLowerCase().includes(term) ||
          (loc.formattedAddress || '').toLowerCase().includes(term)
        );
      });
  }, [locationList, selectedOrgId, classificationFilter, addressSearchText]);

  const getClassificationLabel = (location) => {
    const designations =
      location.licenses && location.licenses[0] ? location.licenses[0].designations || [] : [];
    const hasMedical = designations.includes('MEDICAL');
    const hasRecreational = designations.includes('RECREATIONAL');
    if (hasMedical && hasRecreational) return 'Both';
    if (hasMedical) return 'Medical';
    if (hasRecreational) return 'Recreational';
    return null;
  };

  // ── Infinite scroll observer ────────────────────────────────────────
  useEffect(() => {
    if (!isTabletOrBelow || !cardSentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCards((prev) => prev + 20);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(cardSentinelRef.current);
    return () => observer.disconnect();
  }, [isTabletOrBelow, activeTab]);

  // Reset visible cards when filters change
  useEffect(() => {
    setVisibleCards(20);
  }, [classificationFilter, addressSearchText]);

  // ── Infinite scroll — audiences ─────────────────────────────────────
  useEffect(() => {
    if (!isTabletOrBelow || !audienceSentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleAudienceCards((prev) => prev + 20);
      },
      { rootMargin: '200px' }
    );
    observer.observe(audienceSentinelRef.current);
    return () => observer.disconnect();
  }, [isTabletOrBelow, activeTab]);

  useEffect(() => { setVisibleAudienceCards(20); }, [audienceSearchText]);

  // ── Infinite scroll — classification groups ─────────────────────────
  useEffect(() => {
    if (!isTabletOrBelow || !classSentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleClassCards((prev) => prev + 20);
      },
      { rootMargin: '200px' }
    );
    observer.observe(classSentinelRef.current);
    return () => observer.disconnect();
  }, [isTabletOrBelow, activeTab]);

  useEffect(() => { setVisibleClassCards(20); }, [classSearchText]);

  const {
    id, name, street, city, zip, state, description, email, contact, url,
    phone, formattedAddress, cart, menuDataKey, lat, lng, start, end,
    attributionWindow, locationKey, designation, licenseIdentifier,
  } = parentState || {};

  useEffect(() => {
    form.setFieldsValue({
      name, description, street, city, state, zip, email, contact, url,
      formattedAddress, cart: cart ? cart : 'Select Cart', menuDataKey,
      locationKey, phone, start, end, attributionWindow, designation,
      licenseIdentifier,
    });
  }, [
    attributionWindow, cart, city, contact, description, designation, email,
    end, form, formattedAddress, licenseIdentifier, locationKey, menuDataKey,
    name, parentState, phone, start, state, street, url, zip,
  ]);

  useEffect(() => {
    searchInputPlacesInit();
  });

  // ── Tab count badges ─────────────────────────────────────────────────
  const getTabCount = useCallback(
    (key) => {
      switch (key) {
        case 'location':
          return locationList
            ? locationList.filter(
                (loc) =>
                  loc.audienceType === 'ATTRIBUTION' &&
                  loc.org &&
                  loc.org.id === selectedOrgId
              ).length
            : 0;
        case 'audience': {
          const edges =
            attributionAudiences &&
            attributionAudiences.audiencesConnection &&
            attributionAudiences.audiencesConnection.edges;
          return edges ? edges.filter((val) => val.node.orgs.length === 1).length : 0;
        }
        case 'classification': {
          const edges =
            complianceAudiences &&
            complianceAudiences.audiencesConnection &&
            complianceAudiences.audiencesConnection.edges;
          return edges ? edges.filter((val) => val.node.orgs.length === 1).length : 0;
        }
        default:
          return 0;
      }
    },
    [locationList, attributionAudiences, complianceAudiences, selectedOrgId]
  );

  // ── Keyboard navigation ──────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e) => {
      const currentIndex = TAB_CONFIG.findIndex((t) => t.key === activeTab);
      let nextIndex;
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          nextIndex = (currentIndex + 1) % TAB_CONFIG.length;
          setActiveTab(TAB_CONFIG[nextIndex].key);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          nextIndex = (currentIndex - 1 + TAB_CONFIG.length) % TAB_CONFIG.length;
          setActiveTab(TAB_CONFIG[nextIndex].key);
          break;
        default:
          break;
      }
    },
    [activeTab]
  );

  // ── Location CRUD (same as LocationTab) ──────────────────────────────
  const toggleForm = (val) => {
    setFormShow(val);
    if (!val) setCurrentStep(1);
  };

  const onCreateNewRetailLocation = (values) => {
    toggleForm(false);
    createLocation(values);
  };

  const onDeleteRetailLocation = (Id) => {
    toggleForm(false);
    message.loading('Action in progress..', 0);
    deleteLocation(Id)
      .then(() => {
        message.destroy();
        message.success('Successfully deleted retail Location');
      })
      .catch(() => {
        message.destroy();
        message.error('Error: Cannot delete retail Location');
      });
  };

  const onCloseForm = () => toggleForm(false);

  const onCreateLocation = () => {
    toggleForm(true);
    resetParentState();
  };

  const onUpdateOrgLocation = (values) => {
    toggleForm(false);
    updateOrgLocation(values)
      .then(() => {
        message.destroy();
        message.success('Successfully updated retail Location');
      })
      .catch(() => {
        message.destroy();
        message.error('Error: Cannot update retail Location');
      });
  };

  const onFinish = (values) => {
    if (lat === null || lng === null || !hasOverlay) {
      message.error('Please search for a location and draw an overlay on the map before submitting.');
      return false;
    }
    id === '' ? onCreateNewRetailLocation(values) : onUpdateOrgLocation(values);
  };

  const handleAttributionAudience = async () => {
    setIsAudienceModalVisible(false);
    if (audienceType === 'COMPLIANCE') {
      onSubmitAudience(classificationTypeValue);
      setClassificationFilter(null);
      setClassificationTypeValue('');
      setAudienceType('');
    } else {
      onSubmitAudience(audienceType);
    }
  };

  const onCloseAudienceForm = () => setIsAudienceModalVisible(false);

  const searchInputPlacesInit = () => {
    const input = document.getElementById('search-input-tl');
    if (input) {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
      });
      var options = { componentRestrictions: MapConfig.componentRestrictions };
      const searchBox = new google.maps.places.Autocomplete(input, options);
      searchBox.addListener('place_changed', () => {
        const place = searchBox.getPlace();
        if (!place || !place.geometry || !place.geometry.location) {
          message.error('Error: Place not found!');
          return;
        }
        const location = place.geometry.location;
        updateFormFields(place);
        setSelectedAddress(place);
        onChange(['lat'], location.lat());
        onChange(['lng'], location.lng());
        setInitialPosition({ latitude: location.lat(), longitude: location.lng() });
      });
    }
  };

  const updateFormFields = (place) => {
    let street = '';
    let city = '';
    let state = '';
    let zip = '';
    let country = '';

    for (const component of place.address_components) {
      const componentType = component.types[0];
      switch (componentType) {
        case 'street_number': street = `${component.long_name} ${street}`; break;
        case 'route': street += component.short_name; break;
        case 'postal_code': zip = component.long_name; break;
        case 'locality':
        case 'postal_town':
        case 'administrative_area_level_2':
          if (!city) city = component.long_name;
          break;
        case 'administrative_area_level_1': state = component.long_name; break;
        case 'country': country = component.short_name; break;
        default: break;
      }
    }

    if (!city) {
      const postalTownComponent = place.address_components.find((c) =>
        c.types.includes('postal_town')
      );
      if (postalTownComponent) city = postalTownComponent.long_name;
    }

    form.setFieldsValue({ formattedAddress: place.formatted_address });

    const formattedState =
      stateMapping[state.replace(/\s+/g, '_').toUpperCase()] ||
      state.replace(/\s+/g, '_').toUpperCase();
    const formattedCountry = countryMapping[country] || country;

    onChange(['street'], street);
    onChange(['city'], city);
    onChange(['state'], formattedState);
    onChange(['zip'], zip);
    onChange(['country'], formattedCountry);
    onChange(['formattedAddress'], place.formatted_address);
  };

  // ── Toolbar buttons for Addresses tab (always visible) ───────────────
  const addressToolbarButtons = (
    <>
      <Select
        placeholder="Classification"
        className="rs-classification-filter"
        value={classificationFilter}
        allowClear
        onChange={(value) => {
          setClassificationFilter(value || null);
          if (value) {
            setClassificationTypeValue(classificationToAudienceType[value]);
            setAudienceType('COMPLIANCE');
          } else {
            setClassificationTypeValue('');
            setAudienceType('');
          }
          onChangeStateValue('selectedLocations', []);
        }}
      >
        <Select.Option value="MEDICAL">Medical</Select.Option>
        <Select.Option value="RECREATIONAL">Recreational</Select.Option>
        <Select.Option value="BOTH">Both</Select.Option>
      </Select>
      <div className="rs-search">
        <Input
          placeholder="Search locations"
          value={addressSearchText}
          allowClear
          prefix={<SearchOutlined />}
          onChange={(e) => setAddressSearchText(e.target.value)}
        />
      </div>
      {!isTabletOrBelow && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => onCreateLocation()}
        >
          Create Location
        </Button>
      )}
    </>
  );

  // ── Tab active config ────────────────────────────────────────────────
  const activeTabConfig = TAB_CONFIG.find((t) => t.key === activeTab);
  const ActiveIcon = activeTabConfig ? activeTabConfig.icon : null;
  const activeCount = getTabCount(activeTab);

  return (
    <div>
      {/* ─── Tab bar: pills left, toolbar right ─────────────────────── */}
      <div className="rs-tabs">
        {useDropdown ? (
          <div className="rs-tabs__dropdown" ref={dropdownRef}>
            <button
              type="button"
              className="rs-tabs__dropdown-trigger"
              onClick={() => setDropdownOpen((prev) => !prev)}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
            >
              <span className="rs-tabs__dropdown-icon">
                {ActiveIcon && <ActiveIcon />}
              </span>
              <span className="rs-tabs__dropdown-label">{activeTabConfig?.label}</span>
              {activeCount > 0 && (
                <span className="rs-tabs__dropdown-count">{activeCount}</span>
              )}
              <DownOutlined
                className={`rs-tabs__dropdown-chevron${dropdownOpen ? ' rs-tabs__dropdown-chevron--open' : ''}`}
              />
            </button>
            {dropdownOpen && (
              <>
                <div className="rs-tabs__dropdown-backdrop" onClick={() => setDropdownOpen(false)} />
                <ul className="rs-tabs__dropdown-menu" role="listbox">
                  {TAB_CONFIG.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.key;
                    const count = getTabCount(tab.key);
                    return (
                      <li
                        key={tab.key}
                        role="option"
                        aria-selected={isActive}
                        className={`rs-tabs__dropdown-item${isActive ? ' rs-tabs__dropdown-item--active' : ''}`}
                        onClick={() => { setActiveTab(tab.key); setDropdownOpen(false); }}
                      >
                        <span className="rs-tabs__dropdown-item-icon"><Icon /></span>
                        <span className="rs-tabs__dropdown-item-label">{tab.label}</span>
                        {count > 0 && <span className="rs-tabs__dropdown-item-count">{count}</span>}
                        {isActive && <span className="rs-tabs__dropdown-check">&#10003;</span>}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        ) : (
          <nav
            ref={navRef}
            className="rs-tabs__nav"
            role="tablist"
            aria-label="Retail Stores sections"
            onKeyDown={handleKeyDown}
          >
            {TAB_CONFIG.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              const count = getTabCount(tab.key);
              return (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`rs-panel-${tab.key}`}
                  id={`rs-tab-${tab.key}`}
                  tabIndex={isActive ? 0 : -1}
                  className={`rs-tabs__tab${isActive ? ' rs-tabs__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <span className="rs-tabs__tab-icon"><Icon /></span>
                  <span className="rs-tabs__tab-label">{tab.label}</span>
                  {count > 0 && <span className="rs-tabs__tab-count">{count}</span>}
                </button>
              );
            })}
          </nav>
        )}
        <div ref={setToolbarContainer} className="rs-tabs__toolbar" />
      </div>

      {/* ─── Tab content ────────────────────────────────────────────── */}
      <div
        role="tabpanel"
        id={`rs-panel-${activeTab}`}
        aria-labelledby={`rs-tab-${activeTab}`}
        className="rs-tabs__panel"
      >
        {/* ─── ADDRESSES TAB ─────────────────────────────────────── */}
        {activeTab === 'location' && (
          <div>
            {toolbarContainer && ReactDOM.createPortal(addressToolbarButtons, toolbarContainer)}

            {/* ─── Delete confirmation modal ─────────────────────────── */}
            {isDeleteBulk && hasSelection && (
              <Modal
                title="Confirm Action"
                visible={isDeleteBulk}
                onOk={() => {
                  deleteLocation(locationIds);
                  setIsDeleteBulk(false);
                  handleClearSelection();
                }}
                onCancel={() => setIsDeleteBulk(false)}
                wrapClassName="retail-stores-details-modal"
              >
                <p>
                  Are you sure you want to delete{' '}
                  {`${selectedLocations.length} ${
                    selectedLocations.length === 1 ? 'location' : 'locations'
                  }`}
                  ?
                </p>
              </Modal>
            )}

            {/* ─── Selection alert bar ───────────────────────────────── */}
            {hasSelection && (
              <div className="rs-selection-alert" role="status" aria-live="polite">
                <div className="rs-selection-alert__info">
                  <span className="rs-selection-alert__count">{selectedLocations.length}</span>
                  <span className="rs-selection-alert__text">
                    location{selectedLocations.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="rs-selection-alert__actions">
                  <button
                    type="button"
                    className="rs-selection-alert__link"
                    onClick={() => {
                      setAudienceType('ATTRIBUTION');
                      setIsAudienceModalVisible(true);
                    }}
                  >
                    <PlusOutlined className="rs-selection-alert__link-icon" />
                    Create Audience
                  </button>
                  <span className="rs-selection-alert__separator">&middot;</span>
                  <button
                    type="button"
                    className={`rs-selection-alert__link${!classificationFilter ? ' rs-selection-alert__link--disabled' : ''}`}
                    disabled={!classificationFilter}
                    onClick={() => setIsAudienceModalVisible(true)}
                  >
                    <PlusOutlined className="rs-selection-alert__link-icon" />
                    {classificationFilter
                      ? `Create ${classificationFilterLabels[classificationFilter]} Group`
                      : 'Create Classification Group'}
                  </button>
                  <span className="rs-selection-alert__separator">&middot;</span>
                  <button
                    type="button"
                    className="rs-selection-alert__link rs-selection-alert__link--danger"
                    onClick={() => setIsDeleteBulk(true)}
                  >
                    <DeleteOutlined className="rs-selection-alert__link-icon" />
                    Delete
                  </button>
                  <button
                    type="button"
                    className="rs-selection-alert__close"
                    onClick={handleClearSelection}
                    aria-label="Clear selection"
                  >
                    <CloseOutlined />
                  </button>
                </div>
              </div>
            )}

            {formShow && (
              <Modal
                visible={formShow}
                title={currentStep === 1 ? 'Create Location' : 'Location Boundary Area'}
                onOk={form.submit}
                onCancel={onCloseForm}
                footer={null}
                wrapClassName="retail-stores-create-modal"
                bodyStyle={currentStep === 2 ? { padding: 0 } : {}}
              >
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  autoComplete="off"
                  initialValues={{
                    name, description, street, city, state, zip, email,
                    contact, url, cart: cart ? cart : 'Select Cart',
                    menuDataKey, phone, locationKey,
                  }}
                >
                  {currentStep === 1 ? (
                    <FirstFormPage form={form} onChange={onChange} isSelfService={isSelfService} />
                  ) : (
                    <SecondFormPage
                      form={form}
                      onChange={onChange}
                      selectedAddress={selectedAddress}
                      shouldClearShapes={shouldClearShapes}
                      toggleShouldClearShapes={toggleShouldClearShapes}
                      changeLocationMap={changeLocationMap}
                      initialPosition={initialPosition}
                      setHasOverlay={setHasOverlay}
                      hasOverlay={hasOverlay}
                    />
                  )}
                  <Form.Item>
                    <div className="rs-form-actions">
                      {currentStep > 1 && (
                        <Button onClick={() => setCurrentStep(currentStep - 1)}>Back</Button>
                      )}
                      {currentStep < 2 && (
                        <Button
                          onClick={() => setCurrentStep(currentStep + 1)}
                          disabled={!name || !street || !designation}
                        >
                          Next
                        </Button>
                      )}
                      {currentStep === 2 && (
                        <Button
                          type="primary"
                          htmlType="submit"
                          disabled={lat === null || !hasOverlay}
                        >
                          Submit
                        </Button>
                      )}
                    </div>
                  </Form.Item>
                </Form>
              </Modal>
            )}

            {/* Desktop: table (Targeting pattern) */}
            {!isTabletOrBelow && (
              <div className="rs-address__table">
                {filteredAddresses.length ? (
                  <PaginationTable
                    columns={(() => {
                      const cols = [
                        {
                          title: 'Name',
                          dataIndex: 'name',
                          key: 'name',
                          sorter: (a, b) => a.name.localeCompare(b.name),
                          render: (name, record) => (
                            <div className="rs-loc-row__name">
                              <div className="rs-loc-row__name-text">
                                <Tooltip title={name}>{name}</Tooltip>
                              </div>
                            </div>
                          ),
                        },
                        {
                          title: 'Address',
                          dataIndex: 'formattedAddress',
                          key: 'formattedAddress',
                          ellipsis: true,
                          render: (addr, record) => {
                            const cleaned = addr ? addr.trim().replace(/^, */, '') : null;
                            if (!cleaned) return null;
                            return (
                              <Tooltip title={cleaned}>
                                <span className="rs-loc-row__address">
                                  {record.geoframe === null && <span className="rs-loc-row__no-geo">⚠ </span>}
                                  {cleaned}
                                </span>
                              </Tooltip>
                            );
                          },
                        },
                        {
                          title: 'Classification',
                          dataIndex: 'licenses',
                          key: 'classification',
                          width: 140,
                          render: (_, record) => {
                            const label = getClassificationLabel(record);
                            if (!label) return <span className="rs-loc-row__classification">-</span>;
                            const color = label === 'Medical' ? '#16a34a'
                              : label === 'Recreational' ? '#2563eb'
                              : label === 'Both' ? '#7c3aed' : '#666';
                            return (
                              <span
                                className="rs-loc-row__classification-badge"
                                style={{
                                  background: `${color}14`,
                                  color,
                                  border: `1px solid ${color}33`,
                                }}
                              >
                                {label}
                              </span>
                            );
                          },
                        },
                        {
                          title: 'Created On',
                          dataIndex: 'createdAt',
                          key: 'createdAt',
                          width: 180,
                          render: (text) => (
                            <span className="rs-loc-row__date">
                              {moment(text).utcOffset(0).format('MM/DD/YY - HH:mm [GMT]')}
                            </span>
                          ),
                          sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
                        },
                      ];
                      if (isSelfService) {
                        cols.splice(2, 0, {
                          title: 'Location Type',
                          dataIndex: 'locationType',
                          key: 'locationType',
                          width: 130,
                          render: (_, record) => (
                            <span className="rs-loc-row__type">
                              {record.locationKey ? 'Retail Store' : 'Walk-In'}
                            </span>
                          ),
                        });
                      }
                      return cols;
                    })()}
                    data={filteredAddresses}
                    bordered={false}
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys: selectedLocations ? selectedLocations.map((loc) => loc.id) : [],
                      getCheckboxProps: (record) => ({
                        disabled: record.geoframe === null,
                      }),
                      onChange: (keys, rows) => {
                        onChangeStateValue('selectedLocations', rows);
                      },
                    }}
                    paginationSize="default"
                    scroll={{ x: 600 }}
                    size="small"
                    current={addressPage}
                    pageSize={10}
                    onChange={(p) => setAddressPage(p.current)}
                    onRow={(record) => ({
                      onClick: () => history.push(`/retails_stores/details/${record.id}`),
                    })}
                    rowKey="id"
                  />
                ) : (
                  <Empty />
                )}
              </div>
            )}

            {/* Mobile: card list with infinite scroll */}
            {isTabletOrBelow && (
              <>
                {filteredAddresses.length > 0 ? (
                  <div className="rs-address-cards">
                    {filteredAddresses.slice(0, visibleCards).map((loc) => {
                      const classification = getClassificationLabel(loc);
                      const isSelected = selectedLocations.some((s) => s.id === loc.id);
                      const isExpanded = expandedCardId === loc.id;
                      const classColor = classification === 'Medical' ? '#16a34a'
                        : classification === 'Recreational' ? '#2563eb'
                        : classification === 'Both' ? '#7c3aed' : null;
                      return (
                        <div
                          key={loc.id}
                          className={`rs-address-card${isSelected ? ' rs-address-card--selected' : ''}`}
                        >
                          <div
                            className="rs-address-card__body"
                            onClick={() => history.push(`/retails_stores/details/${loc.id}`)}
                          >
                            <div
                              className="rs-address-card__checkbox"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                checked={isSelected}
                                disabled={loc.geoframe === null}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...selectedLocations, loc]
                                    : selectedLocations.filter((s) => s.id !== loc.id);
                                  onChangeStateValue('selectedLocations', next);
                                }}
                              />
                            </div>
                            <div className="rs-address-card__content">
                              <div className="rs-address-card__icons">
                                {loc.locationKey
                                  ? <ShopOutlined title="Retail Store" />
                                  : <LoginOutlined title="Walk-In" />
                                }
                                {classification === 'Medical' && <MedicineBoxOutlined title="Medical" style={{ color: '#16a34a' }} />}
                                {classification === 'Recreational' && <CoffeeOutlined title="Recreational" style={{ color: '#2563eb' }} />}
                                {classification === 'Both' && <AppstoreOutlined title="Medical & Recreational" style={{ color: '#7c3aed' }} />}
                              </div>
                              <div className="rs-address-card__name">{loc.name}</div>
                              <div className="rs-address-card__badges">
                                {classification && (
                                  <span
                                    className="rs-address-card__badge"
                                    style={{
                                      background: `${classColor}1A`,
                                      color: classColor,
                                      border: `1px solid ${classColor}33`,
                                    }}
                                  >
                                    {classification}
                                  </span>
                                )}
                                {loc.geoframe === null && (
                                  <span className="rs-address-card__badge rs-address-card__badge--warning">
                                    No Geo Target
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="rs-address-card__expand"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedCardId(isExpanded ? null : loc.id);
                            }}
                          >
                            {isExpanded ? 'Less info' : 'More info'}
                            <span className={`rs-address-card__chevron${isExpanded ? ' rs-address-card__chevron--open' : ''}`}>
                              &#9662;
                            </span>
                          </button>

                          {isExpanded && (
                            <div className="rs-address-card__details">
                              {loc.formattedAddress && (
                                <div className="rs-address-card__detail-row">
                                  <span className="rs-address-card__detail-label">Address</span>
                                  <span className="rs-address-card__detail-value">
                                    {loc.formattedAddress.trim().replace(/^, */, '')}
                                  </span>
                                </div>
                              )}
                              <div className="rs-address-card__detail-row">
                                <span className="rs-address-card__detail-label">Created</span>
                                <span className="rs-address-card__detail-value">
                                  {moment(loc.createdAt).utcOffset(0).format('MM/DD/YY - HH:mm [GMT]')}
                                </span>
                              </div>
                              {isSelfService && (
                                <div className="rs-address-card__detail-row">
                                  <span className="rs-address-card__detail-label">Type</span>
                                  <span className="rs-address-card__detail-value">
                                    {loc.locationKey ? 'Retail Store' : 'Walk-In'}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {visibleCards < filteredAddresses.length && (
                      <div ref={cardSentinelRef} className="rs-address-cards__sentinel" />
                    )}
                  </div>
                ) : (
                  <Empty />
                )}

                {/* FAB — Create Location */}
                <button
                  type="button"
                  className="rs-fab"
                  onClick={() => onCreateLocation()}
                  aria-label="Create Location"
                >
                  <PlusOutlined />
                </button>
              </>
            )}

            {isAudienceModalVisible && (
              <CreateAudienceForm
                attributionAudienceName={attributionAudienceName}
                isFormOpen={isAudienceModalVisible}
                closeForm={onCloseAudienceForm}
                onChangeStateValue={onChangeStateValue}
                submitForm={handleAttributionAudience}
                showClassificationSelect={false}
                onClassificationChange={setClassificationTypeValue}
                isClassificationGroup={audienceType === 'COMPLIANCE'}
              />
            )}
          </div>
        )}

        {/* ─── AUDIENCES TAB ─────────────────────────────────────── */}
        {activeTab === 'audience' && (
          <div className="rs-audience">
            {toolbarContainer && ReactDOM.createPortal(
              <div className="rs-search">
                <Input
                  placeholder="Search audiences"
                  value={audienceSearchText}
                  allowClear
                  prefix={<SearchOutlined />}
                  onChange={(e) => setAudienceSearchText(e.target.value)}
                />
              </div>,
              toolbarContainer
            )}

            {/* Delete confirmation modal */}
            {isAudienceDeleteOpen && hasAudienceSelection && (
              <Modal
                title="Confirm Action"
                visible={isAudienceDeleteOpen}
                onOk={onDeleteAudiences}
                onCancel={() => setIsAudienceDeleteOpen(false)}
                wrapClassName="retail-stores-details-modal"
              >
                <p>
                  Are you sure you want to delete{' '}
                  {`${audienceSelectedRows.length} ${
                    audienceSelectedRows.length === 1 ? 'audience' : 'audiences'
                  }`}
                  ?
                </p>
              </Modal>
            )}

            {/* Selection alert bar */}
            {hasAudienceSelection && (
              <div className="rs-selection-alert" role="status" aria-live="polite">
                <div className="rs-selection-alert__info">
                  <span className="rs-selection-alert__count">{audienceSelectedRows.length}</span>
                  <span className="rs-selection-alert__text">
                    audience{audienceSelectedRows.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="rs-selection-alert__actions">
                  <button
                    type="button"
                    className="rs-selection-alert__link rs-selection-alert__link--danger"
                    onClick={() => setIsAudienceDeleteOpen(true)}
                  >
                    <DeleteOutlined className="rs-selection-alert__link-icon" />
                    Delete
                  </button>
                  <button
                    type="button"
                    className="rs-selection-alert__close"
                    onClick={() => setAudienceSelectedRows([])}
                    aria-label="Clear selection"
                  >
                    <CloseOutlined />
                  </button>
                </div>
              </div>
            )}

            {/* Desktop: table */}
            {!isTabletOrBelow && (
              filteredAudienceRows.length ? (
                <div className="rs-audience__table">
                  <PaginationTable
                    columns={AUDIENCE_COLUMNS}
                    data={filteredAudienceRows}
                    bordered={false}
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys: audienceSelectedRows.map((r) => r.id),
                      onChange: (keys, rows) => setAudienceSelectedRows(rows),
                    }}
                    expandedRowRender={(record) => <ExpandedLocations record={record} />}
                    paginationSize="default"
                    scroll={{ x: 600 }}
                    size="small"
                    current={audiencePage}
                    pageSize={10}
                    onChange={(p) => setAudiencePage(p.current)}
                    rowKey="id"
                  />
                </div>
              ) : (
                <Empty />
              )
            )}

            {/* Tablet/mobile: cards (Targeting audience pattern) */}
            {isTabletOrBelow && (
              filteredAudienceRows.length ? (
                <div className="rs-audience-cards">
                  {filteredAudienceRows.slice(0, visibleAudienceCards).map((aud) => {
                    const isSelected = audienceSelectedRows.some((s) => s.id === aud.id);
                    const isExpanded = expandedAudienceCardId === aud.id;
                    const locationCount = aud.geoTargets
                      ? aud.geoTargets.filter((t) => t.location).length
                      : 0;
                    return (
                      <div
                        key={aud.id}
                        className={`rs-audience-card${isSelected ? ' rs-audience-card--selected' : ''}`}
                      >
                        <div
                          className="rs-audience-card__card"
                          onClick={() => {
                            const next = isSelected
                              ? audienceSelectedRows.filter((s) => s.id !== aud.id)
                              : [...audienceSelectedRows, aud];
                            setAudienceSelectedRows(next);
                          }}
                        >
                          <div className="rs-audience-card__header">
                            <span className="rs-audience-card__name">{aud.name}</span>
                          </div>
                          <div className="rs-audience-card__meta">
                            <span className="rs-audience-card__stat">
                              <EnvironmentOutlined /> {locationCount} location{locationCount !== 1 ? 's' : ''}
                            </span>
                            <span className="rs-audience-card__stat">
                              <CalendarOutlined /> {moment(aud.createdAt).format('MMM DD, YYYY')}
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="rs-audience-card__expand"
                          onClick={() => setExpandedAudienceCardId(isExpanded ? null : aud.id)}
                        >
                          {isExpanded ? 'Hide locations' : `View ${locationCount} location${locationCount !== 1 ? 's' : ''}`}
                          <span className={`rs-audience-card__chevron${isExpanded ? ' rs-audience-card__chevron--open' : ''}`}>
                            &#9662;
                          </span>
                        </button>

                        {isExpanded && (
                          locationCount > 0 ? (
                            <div className="rs-audience-card__locations">
                              {aud.geoTargets.filter((t) => t.location).map((t) => (
                                <div key={t.id} className="rs-audience-card__location-chip">
                                  <EnvironmentOutlined className="rs-audience-card__location-icon" />
                                  <span className="rs-audience-card__location-name">{t.name}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="rs-audience-card__locations-empty">No locations</div>
                          )
                        )}
                      </div>
                    );
                  })}
                  {visibleAudienceCards < filteredAudienceRows.length && (
                    <div ref={audienceSentinelRef} className="rs-audience-cards__sentinel" />
                  )}
                </div>
              ) : (
                <Empty />
              )
            )}
          </div>
        )}

        {/* ─── CLASSIFICATION GROUPS TAB ─────────────────────────── */}
        {activeTab === 'classification' && (
          <div className="rs-classification">
            {toolbarContainer && ReactDOM.createPortal(
              <div className="rs-search">
                <Input
                  placeholder="Search classification groups"
                  value={classSearchText}
                  allowClear
                  prefix={<SearchOutlined />}
                  onChange={(e) => setClassSearchText(e.target.value)}
                />
              </div>,
              toolbarContainer
            )}

            {/* Delete confirmation modal */}
            {isClassDeleteOpen && hasClassSelection && (
              <Modal
                title="Confirm Action"
                visible={isClassDeleteOpen}
                onOk={onDeleteClassifications}
                onCancel={() => setIsClassDeleteOpen(false)}
                wrapClassName="retail-stores-details-modal"
              >
                <p>
                  Are you sure you want to delete{' '}
                  {`${classSelectedRows.length} classification ${
                    classSelectedRows.length === 1 ? 'group' : 'groups'
                  }`}
                  ?
                </p>
              </Modal>
            )}

            {/* Selection alert bar */}
            {hasClassSelection && (
              <div className="rs-selection-alert" role="status" aria-live="polite">
                <div className="rs-selection-alert__info">
                  <span className="rs-selection-alert__count">{classSelectedRows.length}</span>
                  <span className="rs-selection-alert__text">
                    classification group{classSelectedRows.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="rs-selection-alert__actions">
                  <button
                    type="button"
                    className="rs-selection-alert__link rs-selection-alert__link--danger"
                    onClick={() => setIsClassDeleteOpen(true)}
                  >
                    <DeleteOutlined className="rs-selection-alert__link-icon" />
                    Delete
                  </button>
                  <button
                    type="button"
                    className="rs-selection-alert__close"
                    onClick={() => setClassSelectedRows([])}
                    aria-label="Clear selection"
                  >
                    <CloseOutlined />
                  </button>
                </div>
              </div>
            )}

            {/* Desktop: table */}
            {!isTabletOrBelow && (
              filteredClassRows.length ? (
                <div className="rs-classification__table">
                  <PaginationTable
                    columns={CLASSIFICATION_COLUMNS}
                    data={filteredClassRows}
                    bordered={false}
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys: classSelectedRows.map((r) => r.id),
                      onChange: (keys, rows) => setClassSelectedRows(rows),
                    }}
                    expandedRowRender={(record) => <ExpandedLocations record={record} />}
                    paginationSize="default"
                    scroll={{ x: 600 }}
                    size="small"
                    current={classPage}
                    pageSize={10}
                    onChange={(p) => setClassPage(p.current)}
                    rowKey="id"
                  />
                </div>
              ) : (
                <Empty />
              )
            )}

            {/* Tablet/mobile: cards (Targeting audience pattern) */}
            {isTabletOrBelow && (
              filteredClassRows.length ? (
                <div className="rs-classification-cards">
                  {filteredClassRows.slice(0, visibleClassCards).map((grp) => {
                    const isSelected = classSelectedRows.some((s) => s.id === grp.id);
                    const isExpanded = expandedClassCardId === grp.id;
                    const typeConfig = classificationTagConfig[grp.type] || { color: 'default', label: grp.type };
                    const typeColor = grp.type === 'COMPLIANCE_MEDICAL' ? '#16a34a'
                      : grp.type === 'COMPLIANCE_RECREATIONAL' ? '#2563eb'
                      : grp.type === 'COMPLIANCE_BOTH' ? '#7c3aed' : '#666';
                    const locationCount = grp.geoTargets
                      ? grp.geoTargets.filter((t) => t.location).length
                      : 0;
                    return (
                      <div
                        key={grp.id}
                        className={`rs-classification-card${isSelected ? ' rs-classification-card--selected' : ''}`}
                      >
                        <div
                          className="rs-classification-card__card"
                          onClick={() => {
                            const next = isSelected
                              ? classSelectedRows.filter((s) => s.id !== grp.id)
                              : [...classSelectedRows, grp];
                            setClassSelectedRows(next);
                          }}
                        >
                          <div className="rs-classification-card__header">
                            <span className="rs-classification-card__name">{grp.name}</span>
                            <span
                              className="rs-classification-card__badge"
                              style={{
                                background: `${typeColor}1A`,
                                color: typeColor,
                                border: `1px solid ${typeColor}33`,
                              }}
                            >
                              {typeConfig.label}
                            </span>
                          </div>
                          <div className="rs-classification-card__meta">
                            <span className="rs-classification-card__stat">
                              <EnvironmentOutlined /> {locationCount} location{locationCount !== 1 ? 's' : ''}
                            </span>
                            <span className="rs-classification-card__stat">
                              <CalendarOutlined /> {moment(grp.createdAt).format('MMM DD, YYYY')}
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="rs-classification-card__expand"
                          onClick={() => setExpandedClassCardId(isExpanded ? null : grp.id)}
                        >
                          {isExpanded ? 'Hide locations' : `View ${locationCount} location${locationCount !== 1 ? 's' : ''}`}
                          <span className={`rs-classification-card__chevron${isExpanded ? ' rs-classification-card__chevron--open' : ''}`}>
                            &#9662;
                          </span>
                        </button>

                        {isExpanded && (
                          locationCount > 0 ? (
                            <div className="rs-classification-card__locations">
                              {grp.geoTargets.filter((t) => t.location).map((t) => (
                                <div key={t.id} className="rs-classification-card__location-chip">
                                  <EnvironmentOutlined className="rs-classification-card__location-icon" />
                                  <span className="rs-classification-card__location-name">{t.name}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="rs-classification-card__locations-empty">No locations</div>
                          )
                        )}
                      </div>
                    );
                  })}
                  {visibleClassCards < filteredClassRows.length && (
                    <div ref={classSentinelRef} className="rs-classification-cards__sentinel" />
                  )}
                </div>
              ) : (
                <Empty />
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RetailStoresLocationTab;
